import { useMemo, useState } from 'react';
import {
  Typography,
  Button,
  Box,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ViewList as BatchIcon,
  ViewModule as SingleIcon,
} from '@mui/icons-material';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { PricingWizardContextValue } from '../types';
import { PricingBatchItem } from './PricingBatchItem';

interface PricingBatchModeProps {
  units: Unit[];
  selectedUnits: Unit[];
  leases: Lease[];
  allLeases: Lease[];
  leaseMap: Map<string, Lease>;
  wizard: PricingWizardContextValue;
}

export function PricingBatchMode({
  units,
  selectedUnits,
  leases,
  allLeases,
  leaseMap,
  wizard,
}: PricingBatchModeProps) {
  const { state, actions, pricedUnitsCount, totalSelectedCount } = wizard;
  const [expandedUnitId, setExpandedUnitId] = useState<string | null>(null);

  // Sort units - unpriced first, then by name
  const sortedUnits = useMemo(() => {
    return [...selectedUnits].sort((a, b) => {
      const aPriced = state.unitPricingData.get(a.id)?.isPriced || false;
      const bPriced = state.unitPricingData.get(b.id)?.isPriced || false;

      if (aPriced !== bPriced) {
        return aPriced ? 1 : -1; // Unpriced first
      }

      return (a.name || a.id).localeCompare(b.name || b.id);
    });
  }, [selectedUnits, state.unitPricingData]);

  const handleToggleExpand = (unitId: string) => {
    setExpandedUnitId(expandedUnitId === unitId ? null : unitId);
  };

  if (selectedUnits.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Typography variant="body1" color="textSecondary">
          No units selected
        </Typography>
      </div>
    );
  }

  return (
    <div className="pricing-batch-mode h-full flex flex-col min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 px-8 py-3 border-b border-zinc-700 flex items-center justify-between">
        <Typography variant="body2" color="textSecondary">
          Enter pricing for each unit below. Click the expand button to see comparables.
        </Typography>
        <div className="flex items-center gap-3">
          {/* Progress indicator */}
          <div className="flex items-center gap-2">
            <Typography variant="body2" className="text-zinc-400">
              <strong className="text-zinc-200">{pricedUnitsCount}</strong>/{totalSelectedCount} priced
            </Typography>
            <Box sx={{ width: 80 }}>
              <LinearProgress
                variant="determinate"
                value={totalSelectedCount > 0 ? (pricedUnitsCount / totalSelectedCount) * 100 : 0}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </div>

          {/* Mode toggle */}
          <ToggleButtonGroup
            value={state.pricingMode}
            exclusive
            onChange={(_, value) => {
              if (value !== null) {
                actions.setPricingMode(value);
              }
            }}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value="single" sx={{ px: 1.5, py: 0.5 }}>
              <SingleIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Single
            </ToggleButton>
            <ToggleButton value="batch" sx={{ px: 1.5, py: 0.5 }}>
              <BatchIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Batch
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="text"
            size="small"
            onClick={() => setExpandedUnitId(null)}
            disabled={!expandedUnitId}
          >
            Collapse All
          </Button>
        </div>
      </div>

      {/* Batch list */}
      <div className="flex-1 min-h-0 overflow-auto px-8 py-4">
        <div className="flex flex-col gap-2">
          {sortedUnits.map((unit) => (
            <PricingBatchItem
              key={unit.id}
              unit={unit}
              units={units}
              lease={leaseMap.get(unit.id)}
              allLeases={allLeases}
              leaseMap={leaseMap}
              wizard={wizard}
              isExpanded={expandedUnitId === unit.id}
              onToggleExpand={() => handleToggleExpand(unit.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
